<template>
  <div class="merged-user-member-list">
    <h1 class="page-title">会员用户管理</h1>
    
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>会员列表</h2>
        </div>
      </template>
      
      <div class="card-filter">
        <el-form :inline="true" :model="searchParams" label-position="left">
          <el-form-item label="会员类型">
            <el-select v-model="searchParams.member_type" placeholder="请选择会员类型" clearable @change="fetchMembers" style="width: 200px;">
              <el-option
                v-for="level in memberLevelOptions"
                :key="level.id"
                :label="level.level_name"
                :value="level.id"
              ></el-option>
            </el-select>
          </el-form-item>
          
          <el-form-item label="搜索">
            <el-input
              v-model="searchParams.keyword"
              placeholder="请输入姓名/手机号"
              clearable
              @keyup.enter="fetchMembers"
            >
              <template #append>
                <el-button @click="fetchMembers">
                  <el-icon><Search /></el-icon>
                </el-button>
              </template>
            </el-input>
          </el-form-item>
          
          <el-form-item>
            <el-button type="default" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
      </div>
      
      <div class="card-table">
        <el-table
          v-loading="loading"
          :data="members"
          border
          style="width: 100%"
        >
          <el-table-column prop="member_type" label="会员类型" width="120" align="center">
            <template #default="scope">
              <el-tag :type="getMemberTypeTag(scope.row.member_type)">
                {{ scope.row.member_type_name || '未知等级' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="nickname" label="姓名" width="120" align="center"></el-table-column>
          <el-table-column prop="status" label="状态" width="100" align="center">
            <template #default="scope">
              <el-tag :type="scope.row.status === 'active' ? 'success' : 'danger'">
                {{ scope.row.status === 'active' ? '正常' : '禁用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="phone" label="手机号" width="150" align="center"></el-table-column>
          <el-table-column prop="member_expire_at" label="会员到期时间" width="180" align="center">
            <template #default="scope">
              {{ scope.row.member_expire_at || '永久' }}
            </template>
          </el-table-column>
          <el-table-column prop="points_balance" label="积分余额" width="120" align="center">
            <template #default="scope">
              {{ Number(scope.row.points_balance) || 0 }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="360" fixed="right" align="center">
            <template #default="scope">
              <el-button type="primary" size="small" @click="handleView(scope.row)">
                <el-icon><View /></el-icon> 详情
              </el-button>
              <el-button type="warning" size="small" @click="handleAdjustPoints(scope.row)">
                <el-icon><Edit /></el-icon> 调整积分
              </el-button>
              <!-- <el-button type="info" size="small" @click="handlePointsInput(scope.row)">
                <el-icon><Star /></el-icon> 消费录入
              </el-button> -->
              <!-- <el-button type="success" size="small" @click="handleViewProfile(scope.row)">
                <el-icon><Picture /></el-icon> 查看档案
              </el-button> -->
            </template>
          </el-table-column>
        </el-table>
        
        <!-- 消费录入对话框 -->
        <el-dialog
          v-model="pointsDialogVisible"
          title="消费录入"
          width="500px"
        >
          <el-form :model="pointsForm" label-width="80px">
            <el-form-item label="金额" required>
              <el-input-number
                v-model="pointsForm.amount"
                :step="0.01"
                :precision="2"
                placeholder="请输入金额"
                style="width: 100%"
              ></el-input-number>
            </el-form-item>
            <el-form-item label="事项" required>
              <el-select
                v-model="pointsForm.matter"
                placeholder="请选择事项"
                style="width: 100%"
              >
                <el-option
                  v-for="option in matterOptions"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value"
                ></el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="计算积分">
              <el-checkbox v-model="pointsForm.calculatePoints">
                是否计算积分
              </el-checkbox>
            </el-form-item>
            <el-form-item v-if="pointsForm.calculatePoints" label="预计积分">
              <div class="estimated-points">
                <span class="text-success font-bold">{{ estimatedPoints }}</span> 积分
              </div>
            </el-form-item>
            <el-form-item label="订单号">
              <el-input
                v-model="pointsForm.orderNumber"
                placeholder="请输入订单号（可选）"
                style="width: 100%"
              ></el-input>
            </el-form-item>
            <el-form-item label="备注">
              <el-input
                v-model="pointsForm.remark"
                type="textarea"
                :rows="3"
                placeholder="请输入备注（可选）"
              ></el-input>
            </el-form-item>
          </el-form>
          <template #footer>
            <span class="dialog-footer">
              <el-button @click="pointsDialogVisible = false">取消</el-button>
              <el-button type="primary" @click="confirmPointsInput">确定</el-button>
            </span>
          </template>
        </el-dialog>

        <!-- 积分调整对话框 -->
        <el-dialog
          v-model="adjustDialogVisible"
          title="调整积分"
          width="500px"
        >
          <div v-if="adjustTarget" class="adjust-info">
            <span>用户：{{ adjustTarget.nickname }}（{{ adjustTarget.phone }}）</span>
            <span>当前余额：<strong>{{ Number(adjustTarget.points_balance) || 0 }}</strong> 积分</span>
          </div>
          <el-form :model="adjustForm" label-width="100px" style="margin-top: 16px;">
            <el-form-item label="调整类型" required>
              <el-radio-group v-model="adjustForm.type">
                <el-radio value="increase">增加</el-radio>
                <el-radio value="decrease">扣减</el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item label="调整数量" required>
              <el-input-number
                v-model="adjustForm.amount"
                :min="1"
                :step="1"
                :precision="0"
                placeholder="请输入积分数量"
                style="width: 100%"
              ></el-input-number>
            </el-form-item>
            <el-form-item label="调整原因" required>
              <el-input
                v-model="adjustForm.reason"
                type="textarea"
                :rows="3"
                placeholder="请输入调整原因"
              ></el-input>
            </el-form-item>
            <el-form-item label="调整后余额">
              <div class="adjust-preview">
                <span class="font-bold" :class="adjustExceedsBalance ? 'text-danger' : 'text-success'">
                  {{ adjustedBalance }}
                </span> 积分
              </div>
              <div v-if="adjustExceedsBalance" class="adjust-warning">
                扣减数量不可超过当前积分余额
              </div>
            </el-form-item>
          </el-form>
          <template #footer>
            <span class="dialog-footer">
              <el-button @click="adjustDialogVisible = false">取消</el-button>
              <el-button
                type="primary"
                :disabled="adjustExceedsBalance || !adjustForm.amount || !adjustForm.reason"
                @click="confirmAdjustPoints"
              >确认调整</el-button>
            </span>
          </template>
        </el-dialog>
        
        <!-- 会员详情抽屉 -->
        <el-drawer
          v-model="drawerVisible"
          title="会员详情"
          direction="rtl"
          size="50%"
        >
          <div v-if="selectedMember" class="member-detail">
            <el-descriptions title="基本信息" :column="1" border>
  
              <el-descriptions-item label="会员类型">
                <el-tag :type="getMemberTypeTag(selectedMember.member_type)">
                  {{ selectedMember.member_type_name || '未知等级' }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="姓名">{{ selectedMember.nickname }}</el-descriptions-item>
              <el-descriptions-item label="手机号">{{ selectedMember.phone }}</el-descriptions-item>
              <el-descriptions-item label="注册时间">{{ selectedMember.registration_date }}</el-descriptions-item>
              <el-descriptions-item label="会员到期时间">{{ selectedMember.member_expire_at || '永久' }}</el-descriptions-item>
              <el-descriptions-item label="状态">
                <el-tag :type="selectedMember.status === 'active' ? 'success' : 'danger'">
                  {{ selectedMember.status === 'active' ? '正常' : '禁用' }}
                </el-tag>
              </el-descriptions-item>
            </el-descriptions>
            
            <!-- 详细信息需要从详情API获取，这里预留位置 -->
            <div v-if="memberDetail" class="mt-4">
              <el-descriptions title="详细信息" :column="1" border>
                <!-- <el-descriptions-item label="微信昵称">{{ memberDetail.wechat_name || '-' }}</el-descriptions-item> -->
                <!-- <el-descriptions-item label="真实姓名">{{ memberDetail.real_name || '-' }}</el-descriptions-item> -->
                <!-- <el-descriptions-item label="性别">{{ memberDetail.gender === 'male' ? '男' : memberDetail.gender === 'female' ? '女' : '-' }}</el-descriptions-item> -->
                <!-- <el-descriptions-item label="生日">{{ memberDetail.birthday || '-' }}</el-descriptions-item> -->
                <el-descriptions-item label="累计消费">{{ '¥' + memberDetail.total_consumption }}</el-descriptions-item>                <el-descriptions-item label="积分余额">{{ memberDetail.points_balance || 0 }}</el-descriptions-item>
                <!-- <el-descriptions-item label="储值余额">{{ memberDetail.prepaid_balance ? '¥' + Number(memberDetail.prepaid_balance).toFixed(2) : '¥0.00' }}</el-descriptions-item> -->
                <!-- <el-descriptions-item label="地址">{{ memberDetail.address || '-' }}</el-descriptions-item> -->
              </el-descriptions>
            </div>
          </div>
        </el-drawer>
        
        <!-- 用户档案右侧抽屉 -->
        <el-drawer
          v-model="profileDialogVisible"
          title="用户档案"
          direction="rtl"
          size="40%"
        >
          <div v-if="currentProfileMember" class="profile-dialog">
            <div class="member-info">
              <h3>{{ currentProfileMember.nickname }} - {{ currentProfileMember.phone }}</h3>
            </div>
            
            <!-- 封面图上传区域 -->
            <div class="cover-image-section">
              <h4>封面图</h4>
              <el-form label-width="100px">
                <el-form-item label="封面图">
                  <el-upload
                    class="cover-uploader"
                    action="#"
                    :show-file-list="true"
                    :on-success="handleCoverUploadSuccess"
                    :on-error="handleCoverUploadError"
                    :before-upload="handleCoverBeforeUpload"
                    :limit="1"
                    :file-list="coverImageFileList"
                  >
                    <img v-if="coverImageUrl" :src="coverImageUrl" class="profile-image" />
                    <el-icon v-else class="profile-uploader-icon"><Plus /></el-icon>
                  </el-upload>
                  <div class="form-tip">支持JPG、PNG格式，大小不超过2MB</div>
                </el-form-item>
              </el-form>
            </div>
            
            <!-- 已上传图片列表 -->
            <div class="uploaded-images-section">
              <h4>已上传图片</h4>
              <el-table
                v-if="uploadedImages.length > 0"
                :data="uploadedImages"
                border
                style="width: 100%; margin-top: 10px;"
                size="small"
                v-loading="profileLoading"
              >
                <el-table-column prop="name" label="图片名称" width="180"></el-table-column>
                <el-table-column label="图片预览" width="120" align="center">
                  <template #default="scope">
                    <el-image
                      :src="scope.row.image_url"
                      fit="cover"
                      style="width: 60px; height: 60px; cursor: pointer;"
                      :preview-src-list="[scope.row.image_url]"
                    ></el-image>
                  </template>
                </el-table-column>
                <el-table-column prop="created_at" label="上传时间" width="200">
                  <template #default="scope">
                    {{ new Date(scope.row.created_at).toLocaleString() }}
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="120" align="center">
                  <template #default="scope">
                    <el-button
                      type="danger"
                      size="small"
                      @click="handleDeleteImage(scope.row.id)"
                    >
                      <el-icon><Delete /></el-icon> 删除
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
              <div v-else class="no-images-tip">
                <el-empty description="暂无已上传图片"></el-empty>
              </div>
            </div>
            
            <!-- 独立的上传组件 -->
            <div class="independent-upload-section">
              <h4>上传新图片</h4>
              <el-form label-width="100px">
                <el-form-item label="新图片">
                  <el-upload
                    class="cover-uploader"
                    action="#"
                    :show-file-list="true"
                    :on-success="handleProfileUploadSuccess"
                    :on-error="handleProfileUploadError"
                    :before-upload="handleOtherBeforeUpload"
                    :limit="1"
                    :file-list="otherFileList"
                  >
                    <el-icon class="profile-uploader-icon"><Plus /></el-icon>
                  </el-upload>
                  <div class="form-tip">支持JPG、PNG格式，大小不超过2MB</div>
                </el-form-item>
              </el-form>
            </div>
            <div class="drawer-footer">
              <el-button @click="profileDialogVisible = false">取消</el-button>
              <el-button type="primary" @click="handleSaveProfile">保存</el-button>
            </div>
          </div>
        </el-drawer>
      </div>
      
      <div class="card-footer">
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import {
  Search, Plus, View, Edit, Delete, Picture
} from '@element-plus/icons-vue';
import * as memberApi from '../../api/member';
import service from '../../api/axios';

// 路由实例
const router = useRouter();

// 加载状态
const loading = ref(true);

// 会员列表数据
const members = ref([]);

// 会员等级选项
const memberLevelOptions = ref([]);

// 搜索参数
const searchParams = ref({
  keyword: '',
  member_type: ''
});

// 分页数据
const currentPage = ref(1);
const pageSize = ref(10);
const total = ref(0);

// 详情抽屉状态
const drawerVisible = ref(false);
const selectedMember = ref(null);
const memberDetail = ref(null);
const detailLoading = ref(false);

// 用户档案对话框状态
const profileDialogVisible = ref(false);
const currentProfileMember = ref(null);
const profile = ref({});
const profileLoading = ref(false);
const uploadedImages = ref([]);

// 封面图相关状态
const coverImageUrl = ref('');
const coverImageFileList = ref([]);
const otherFileList = ref([]);

// 消费录入对话框状态
const pointsDialogVisible = ref(false);
const pointsForm = ref({
  amount: 0,
  matter: '房费录入', // 默认事项
  calculatePoints: true, // 默认计算积分
  orderNumber: '', // 订单号，可选
  remark: '' // 备注，可选
});

// 事项选项
const matterOptions = ref([
  { value: '房费录入', label: '房费录入' }
]);

// 积分比例设置
const pointSettings = ref({
  room_fee_point_ratio: 1.00,
  system_consumption_point_ratio: 1.00
});

// 获取积分设置
const fetchPointSettings = async () => {
  try {
    const response = await memberApi.fetchPointSettings();
    pointSettings.value = response.data;
  } catch (error) {
    console.error('获取积分设置失败:', error);
  }
};

// 预计发放积分
const estimatedPoints = computed(() => {
  if (!pointsForm.value.calculatePoints || pointsForm.value.amount <= 0) {
    return 0;
  }
  
  // 根据事项类型选择积分比例
  const ratio = pointsForm.value.matter === '房费录入' ? pointSettings.value.room_fee_point_ratio : pointSettings.value.system_consumption_point_ratio;
  return Math.floor(pointsForm.value.amount * ratio);
});
const currentMemberId = ref(null);

// 积分来源选项
const pointsSourceOptions = ref([
  { value: 'room_fee', label: '房间费用' },
  { value: 'system_consumption', label: '系统消费' },
  { value: 'luky_cloud', label: '消费系统' },
  { value: 'mall_discount', label: '商城折扣' }
]);

// 积分调整对话框状态
const adjustDialogVisible = ref(false);
const adjustTarget = ref(null);
const adjustForm = ref({
  type: 'increase',
  amount: 1,
  reason: ''
});

// 调整后余额计算
const adjustedBalance = computed(() => {
  if (!adjustTarget.value) return 0;
  const current = Number(adjustTarget.value.points_balance) || 0;
  const amount = adjustForm.value.amount || 0;
  if (adjustForm.value.type === 'increase') {
    return current + amount;
  }
  return Math.max(0, current - amount);
});

// 扣减是否超额
const adjustExceedsBalance = computed(() => {
  if (!adjustTarget.value || adjustForm.value.type === 'increase') return false;
  const current = Number(adjustTarget.value.points_balance) || 0;
  return (adjustForm.value.amount || 0) > current;
});

// 获取会员等级选项
const fetchMemberLevelOptions = async () => {
  try {
    const response = await memberApi.fetchMemberLevelOptions();
    memberLevelOptions.value = response.data || [];
  } catch (error) {
    console.error('获取会员等级选项失败:', error);
    memberLevelOptions.value = [];
  }
};

// 获取会员类型标签
const getMemberTypeTag = (type) => {
  // 根据会员等级ID返回不同的标签类型
  // 这里可以根据实际业务需求调整
  const tagMap = {
    1: '',
    2: 'primary',
    3: 'success',
    4: 'warning',
    5: 'danger'
  };
  return tagMap[type] || '';
};

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString();
};

// 获取会员列表
const fetchMembers = async () => {
  loading.value = true;
  try {
    const response = await memberApi.fetchMemberList({
      page: currentPage.value,
      pageSize: pageSize.value,
      keyword: searchParams.value.keyword,
      member_type: searchParams.value.member_type,
    });
    
    // 确保members始终是数组，避免Element Plus表格出错
    members.value = Array.isArray(response.data) ? response.data : [];
    total.value = response.total || 0;
  } catch (error) {
    ElMessage.error('获取会员列表失败：' + (error.message || '未知错误'));
    console.error('获取会员列表失败:', error);
    // 出错时确保members是数组
    members.value = [];
    total.value = 0;
  } finally {
    loading.value = false;
  }
};

// 搜索
const handleSearch = () => {
  currentPage.value = 1;
  fetchMembers();
};

// 重置搜索条件
const resetSearch = () => {
  searchParams.value = {
    keyword: '',
    member_type: ''
  };
  currentPage.value = 1;
  fetchMembers();
};

// 分页大小变化
const handleSizeChange = (size) => {
  pageSize.value = size;
  fetchMembers();
};

// 当前页变化
const handleCurrentChange = (page) => {
  currentPage.value = page;
  fetchMembers();
};

// 加载数据
onMounted(async () => {
  // 先获取会员等级选项
  await fetchMemberLevelOptions();
  // 获取积分设置
  await fetchPointSettings();
  // 然后获取会员列表
  fetchMembers();
});



// 打开会员详情抽屉
const handleView = async (member) => {
  selectedMember.value = member;
  memberDetail.value = null;
  drawerVisible.value = true;
  
  // 获取详细信息
  await fetchMemberDetail(member.id);
};

// 打开用户档案对话框
const handleViewProfile = async (member) => {
  currentProfileMember.value = member;
  profile.value = {};
  coverFileList.value = [];
  profileDialogVisible.value = true;
  
  // 获取用户档案
  await fetchMemberProfile(member.id);
};

// 获取用户档案（同时获取封面图和档案图片列表）
const fetchMemberProfile = async (id) => {
  profileLoading.value = true;
  try {
    // 获取用户档案（包含封面图）
    const profileResponse = await memberApi.fetchMemberProfile(id);
    profile.value = profileResponse.data || {};
    
    // 设置封面图URL
    coverImageUrl.value = '';
    if (profile.value.image_url) {
      coverImageUrl.value = profile.value.image_url.startsWith('/') 
        ? `${window.location.origin}` + profile.value.image_url 
        : profile.value.image_url;
      coverImageFileList.value = [{ name: '封面图', url: coverImageUrl.value }];
    } else {
      coverImageFileList.value = [];
    }
    
    // 获取用户档案图片列表
    const imagesResponse = await memberApi.fetchMemberProfileImages(id);
    uploadedImages.value = imagesResponse.data || [];
    
    // 为图片URL添加完整域名前缀
    uploadedImages.value.forEach(image => {
      if (image.image_url.startsWith('/')) {
        image.image_url = `${window.location.origin}` + image.image_url;
      }
    });
    
    // 清空其他文件列表
    otherFileList.value = [];
  } catch (error) {
    ElMessage.error('获取用户档案失败：' + (error.message || '未知错误'));
    console.error('获取用户档案失败:', error);
    profile.value = {};
    coverImageUrl.value = '';
    coverImageFileList.value = [];
    uploadedImages.value = [];
    otherFileList.value = [];
  } finally {
    profileLoading.value = false;
  }
};

// 档案图片上传成功处理
const handleProfileUploadSuccess = async (response) => {
  // 适配 wangEditor 格式和标准格式
  let imageUrl = '';
  
  // 注意：响应拦截器已经处理了响应，所以直接检查data
  if (response.data && response.data.length > 0) {
    // wangEditor 格式（响应拦截器已处理，errno === 0）
    imageUrl = response.data[0].url;
  } else if (response.url) {
    // 直接返回URL的情况
    imageUrl = response.url;
  }
  
  if (imageUrl) {
    try {
      // 调用API添加图片记录
      const addResponse = await memberApi.addMemberProfileImage(currentProfileMember.value.id, {
        image_url: imageUrl,
        name: '档案图片'
      });
      
      // 刷新图片列表
      await fetchMemberProfile(currentProfileMember.value.id);
      
      // 清空当前文件列表，允许继续上传新图片
      otherFileList.value = [];
      ElMessage.success('档案图片上传成功');
    } catch (error) {
      console.error('添加图片记录失败:', error);
      ElMessage.error('添加图片记录失败，请重试');
    }
  } else {
    ElMessage.error('档案图片上传失败');
  }
};

// 档案图片上传失败处理
const handleProfileUploadError = (error) => {
  ElMessage.error('图片上传失败，请重试');
};

// 图片上传前验证
const beforeImageUpload = (file) => {
  const isJPG = file.type === 'image/jpeg' || file.type === 'image/png';
  const isLt2M = file.size / 1024 / 1024 < 2;
  
  if (!isJPG) {
    ElMessage.error('上传图片只能是 JPG/PNG 格式!');
    return false;
  }
  if (!isLt2M) {
    ElMessage.error('上传图片大小不能超过 2MB!');
    return false;
  }
  
  return true;
};

// 封面图上传前处理（手动上传）
const handleCoverBeforeUpload = async (file) => {
  // 先验证图片
  if (!beforeImageUpload(file)) {
    return false;
  }
  
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'other');
    
    const response = await service({
      url: '/manage/upload/image',
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    // 调用成功处理函数
    handleCoverUploadSuccess(response);
  } catch (error) {
    // 调用失败处理函数
    handleCoverUploadError(error);
  }
  
  // 阻止默认上传行为
  return false;
};

// 其他图片上传前处理（手动上传）
const handleOtherBeforeUpload = async (file) => {
  // 先验证图片
  if (!beforeImageUpload(file)) {
    return false;
  }
  
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'other');
    
    const response = await service({
      url: '/manage/upload/image',
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    // 调用成功处理函数
    handleProfileUploadSuccess(response);
  } catch (error) {
    // 调用失败处理函数
    handleProfileUploadError(error);
  }
  
  // 阻止默认上传行为
  return false;
};

// 删除图片
const handleDeleteImage = async (id) => {
  try {
    await memberApi.deleteMemberProfileImage(currentProfileMember.value.id, id);
    // 刷新图片列表
    await fetchMemberProfile(currentProfileMember.value.id);
    ElMessage.success('图片删除成功');
  } catch (error) {
    console.error('删除图片失败:', error);
    ElMessage.error('删除图片失败，请重试');
  }
};

// 手动上传处理函数（用于其他图片）
const handleManualUpload = async (options) => {
  const { file, onSuccess, onError } = options;
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'other');
    
    const response = await service({
      url: '/manage/upload/image',
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    // 调用上传成功回调
    onSuccess(response);
  } catch (error) {
    console.error('上传失败详细信息:', error);
    onError(error);
  }
};

// 封面图手动上传处理函数
const handleCoverUpload = async (options) => {
  const { file, onSuccess, onError } = options;
  console.log('开始上传封面图:', file.name);
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'other');
    
    console.log('发送上传请求...');
    const response = await service({
      url: '/manage/upload/image',
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    console.log('上传请求响应:', response);
    // 调用上传成功回调
    onSuccess(response);
  } catch (error) {
    console.error('封面图上传失败:', error);
    console.error('错误详情:', error.response || error.message);
    onError(error);
  }
};

// 封面图上传成功处理
const handleCoverUploadSuccess = async (response) => {
  console.log('封面图上传响应:', response);
  // 适配 wangEditor 格式和标准格式
  let imageUrl = '';
  
  // 直接使用响应数据，不经过复杂处理
  if (response && response.data && response.data.length > 0) {
    imageUrl = response.data[0].url;
  } else if (response && response.url) {
    imageUrl = response.url;
  } else if (response && typeof response === 'object') {
    // 尝试从响应对象中提取URL
    console.log('尝试从响应对象中提取URL:', response);
    imageUrl = response.data?.url || response.url || '';
  }
  
  console.log('提取到的图片URL:', imageUrl);
  
  if (imageUrl) {
    try {
      // 更新封面图
      await memberApi.updateMemberProfile(currentProfileMember.value.id, {
        image_url: imageUrl
      });
      
      // 刷新档案信息
      await fetchMemberProfile(currentProfileMember.value.id);
      
      ElMessage.success('封面图上传成功');
    } catch (error) {
      console.error('更新封面图失败:', error);
      ElMessage.error('更新封面图失败，请重试');
    }
  } else {
    ElMessage.error('封面图上传失败');
  }
};

// 封面图上传失败处理
const handleCoverUploadError = (error) => {
  ElMessage.error('封面图上传失败，请重试');
};

// 保存用户档案
const handleSaveProfile = async () => {
  if (!currentProfileMember.value || !profile.value) return;
  
  profileLoading.value = true;
  try {
    await memberApi.updateMemberProfile(currentProfileMember.value.id, {
      image_url: profile.value.image_url
    });
    ElMessage.success('保存成功');
    profileDialogVisible.value = false;
  } catch (error) {
    ElMessage.error('保存失败：' + (error.message || '未知错误'));
    console.error('保存用户档案失败:', error);
  } finally {
    profileLoading.value = false;
  }
};

// 获取会员详情
const fetchMemberDetail = async (id) => {
  detailLoading.value = true;
  try {
    const response = await memberApi.fetchMemberDetail(id);
    memberDetail.value = response.data;
  } catch (error) {
    ElMessage.error('获取会员详情失败：' + (error.message || '未知错误'));
    console.error('获取会员详情失败:', error);
  } finally {
    detailLoading.value = false;
  }
};

// 跳转到编辑会员页面
const handleEdit = (member) => {
  router.push({
    path: `/member/${member.id}/edit`,
    query: { member: JSON.stringify(member) }
  });
};

// 删除会员
const handleDelete = (member) => {
  ElMessageBox.confirm('确定要删除该会员吗？', '警告', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    try {
      await memberApi.deleteMember(member.id);
      ElMessage.success('删除成功');
      fetchMembers();
    } catch (error) {
      ElMessage.error('删除失败');
      console.error(error);
    }
  }).catch(() => {
    // 取消删除操作
  });
};

// 打开消费录入对话框
const handlePointsInput = (member) => {
  currentMemberId.value = member.id;
  pointsForm.value = {
    amount: 0,
    matter: '房费录入', // 默认事项
    calculatePoints: true, // 默认计算积分
    orderNumber: '', // 订单号，可选
    remark: '' // 备注，可选
  };
  pointsDialogVisible.value = true;
};

// 确认消费录入
const confirmPointsInput = async () => {
  if (!currentMemberId.value) return;
  
  try {
    await memberApi.createConsumption(currentMemberId.value, pointsForm.value);
    ElMessage.success('消费录入成功');
    pointsDialogVisible.value = false;
    fetchMembers(); // 刷新会员列表
  } catch (error) {
    ElMessage.error('消费录入失败：' + (error.message || '未知错误'));
    console.error('消费录入失败:', error);
  }
};

// 打开积分调整对话框
const handleAdjustPoints = (member) => {
  adjustTarget.value = member;
  adjustForm.value = {
    type: 'increase',
    amount: 1,
    reason: ''
  };
  adjustDialogVisible.value = true;
};

// 确认积分调整（二次确认后调用 API）
const confirmAdjustPoints = () => {
  const typeName = adjustForm.value.type === 'increase' ? '增加' : '扣减';
  ElMessageBox.confirm(
    `确认为用户 ${adjustTarget.value.nickname} ${typeName} ${adjustForm.value.amount} 积分？原因：${adjustForm.value.reason}`,
    '确认调整',
    {
      confirmButtonText: '确认',
      cancelButtonText: '取消',
      type: 'warning'
    }
  ).then(async () => {
    try {
      await memberApi.adjustMemberPoints(adjustTarget.value.id, adjustForm.value);
      ElMessage.success('积分调整成功');
      adjustDialogVisible.value = false;
      fetchMembers();
    } catch (error) {
      ElMessage.error('积分调整失败：' + (error.response?.data?.message || error.message || '未知错误'));
      console.error('积分调整失败:', error);
    }
  }).catch(() => {
    // 取消确认
  });
};
</script>

<style scoped>
.merged-user-member-list {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.view-switcher {
  margin-bottom: 20px;
}

.content-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.card-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.card-filter {
  margin-bottom: 20px;
  padding: 10px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.card-table {
  margin-bottom: 20px;
}

.card-footer {
  display: flex;
  justify-content: flex-end;
  padding: 10px 0;
}

/* 用户列表样式 */
.user-list-container {
  padding: 20px;
}

.search-filter {
  margin-bottom: 20px;
}

.user-info {
  display: flex;
  align-items: center;
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 10px;
}

.user-details {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-weight: bold;
  margin-bottom: 2px;
}

.user-phone {
  font-size: 12px;
  color: #666;
}

.estimated-points {
  padding: 10px;
  background-color: #f0f9eb;
  border-radius: 4px;
  text-align: center;
}

.font-bold {
  font-weight: bold;
  font-size: 18px;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

/* 用户档案对话框样式 */
.profile-dialog {
  padding: 20px 0;
}

.member-info h3 {
  margin: 0 0 20px 0;
  font-size: 18px;
  color: #333;
}

.cover-uploader {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.profile-image {
  width: 200px;
  height: 200px;
  object-fit: cover;
  border-radius: 4px;
}

.profile-uploader-icon {
  font-size: 28px;
  color: #409EFF;
  width: 200px;
  height: 200px;
  border: 1px dashed #D9D9D9;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  background-color: #fafafa;
  transition: border-color 0.3s;
}

.profile-uploader-icon:hover {
  border-color: #409EFF;
}

.form-tip {
  margin-top: 10px;
  font-size: 12px;
  color: #999;
  text-align: center;
}

.drawer-footer {
  margin-top: 30px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 10px 0;
  border-top: 1px solid #ebeef5;
}

/* 已上传图片列表样式 */
.uploaded-images-section {
  margin-top: 30px;
  padding: 10px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.uploaded-images-section h4 {
  margin: 0 0 15px 0;
  font-size: 16px;
  color: #333;
  font-weight: bold;
}

.no-images-tip {
  padding: 20px;
  text-align: center;
}

.uploaded-images-section .el-table {
  margin-top: 0;
}

.uploaded-images-section .el-table__header-wrapper {
  background-color: #eef2f7;
}

.uploaded-images-section .el-image {
  border-radius: 4px;
  transition: transform 0.3s;
}

.uploaded-images-section .el-image:hover {
  transform: scale(1.1);
}

/* 封面图区域样式 */
.cover-image-section {
  margin-top: 30px;
  padding: 10px;
  background-color: #e6f7ff;
  border-radius: 4px;
}

.cover-image-section h4 {
  margin: 0 0 15px 0;
  font-size: 16px;
  color: #333;
  font-weight: bold;
}

/* 独立上传区域样式 */
.independent-upload-section {
  margin-top: 30px;
  padding: 10px;
  background-color: #f0f9eb;
  border-radius: 4px;
}

.independent-upload-section h4 {
  margin: 0 0 15px 0;
  font-size: 16px;
  color: #333;
  font-weight: bold;
}

.independent-upload-section .cover-uploader {
  margin-left: 0;
}

/* 积分调整弹窗样式 */
.adjust-info {
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: #f5f7fa;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;
}

.adjust-preview {
  padding: 10px;
  background-color: #f0f9eb;
  border-radius: 4px;
  text-align: center;
}

.adjust-warning {
  margin-top: 6px;
  color: #f56c6c;
  font-size: 12px;
}

.text-danger {
  color: #f56c6c;
}
</style>
